import { useNavigate, useLocation, Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { serviceOrder } from "@/data/servicePages";

export default function Navbar() {
  const { t, lang, base, toggleLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const goTo = (id) => {
    if (id === "insights") { navigate("/insights"); return; }
    if (id === "contact") { navigate("/contact"); return; }
    if (location.pathname === base) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`${base}#${id}`);
    }
  };

  const services = serviceOrder.map((slug, i) => ({ slug, label: t.services.items[i]?.title || slug }));

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-paper/85 border-b border-mist">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => {
            if (location.pathname === base) window.scrollTo({ top: 0, behavior: "smooth" });
            else navigate(base);
          }}
          className="font-display font-bold text-[22px] text-ink tracking-tight"
        >
          AURELIUS<span className="text-brand">.</span>
        </button>
        <nav className="hidden md:flex items-center gap-9">
          {t.nav.links.map((l) =>
            l.id === "services" ? (
              <div key={l.id} className="relative group">
                <button
                  data-testid="nav-link-services"
                  onClick={() => goTo("services")}
                  aria-haspopup="true"
                  className="inline-flex items-center gap-1 text-sm font-medium text-ink/75 hover:text-brand transition-colors"
                >
                  {l.label}
                  <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-hover:rotate-180" strokeWidth={2} />
                </button>
                <div className="absolute top-full start-0 pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-all duration-200">
                  <div className="w-[280px] rounded-2xl border border-mist bg-paper/95 backdrop-blur-xl shadow-xl shadow-brand/10 p-2">
                    {services.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/${s.slug}`}
                        data-testid={`nav-service-${s.slug}`}
                        className="block rounded-xl px-4 py-2.5 text-sm font-medium text-ink/80 hover:bg-brand hover:text-white transition-colors"
                      >
                        {s.label}
                      </Link>
                    ))}
                    <button
                      onClick={() => goTo("services")}
                      className="w-full text-start rounded-xl px-4 py-2.5 mt-1 border-t border-mist text-xs font-bold uppercase tracking-[0.18em] text-brand-700 hover:text-brand transition-colors"
                    >
                      {t.services.overline}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => goTo(l.id)}
                className="text-sm font-medium text-ink/75 hover:text-brand transition-colors"
              >
                {l.label}
              </button>
            )
          )}
        </nav>
        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggleLang}
            className="border border-mist text-ink/70 hover:border-brand hover:text-brand transition-colors text-sm px-3 py-2 rounded-full"
          >
            {lang === "en" ? "عربي" : "EN"}
          </button>
          <button
            data-testid="nav-cta-button"
            onClick={() => goTo("contact")}
            className="bg-brand hover:bg-brand-700 transition-colors text-white text-sm font-semibold px-5 py-2.5 rounded-full"
          >
            {t.nav.cta}
          </button>
        </div>
      </div>
    </header>
  );
}
